import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { CheckCircle, TrendingUp, Users, Award, Globe, Phone, Mail, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

export const FranchiseOpportunity = () => {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const carouselImages = [
    {
      url: "https://customer-assets.emergentagent.com/job_franchise-phoenix/artifacts/nw3hhl35_Franchise%20standing%20outside%20salon%20signage_20251006_181713_0023.png",
      alt: "Franchise Owner Outside Salon"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_franchise-phoenix/artifacts/bvbhnmyh_Banners%20for%20blogs%20%20A%20Double%20Standard%20for%20Black%20Entrepreneurs_20251109_170450_0001.png",
      alt: "Train to be a Cranial Prosthesis Consultant"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_franchise-phoenix/artifacts/ojo0p0ie_Franchise%20standing%20outside%20salon%20signage_20251006_181713_0024.png",
      alt: "Successful Franchise Location"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_franchise-phoenix/artifacts/36r3x6gj_Banners%20for%20blogs%20%20A%20Double%20Standard%20for%20Black%20Entrepreneurs_20251109_170450_0003.png",
      alt: "Hair Loss Solutions Specialist Training"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_franchise-phoenix/artifacts/lljf0082_Franchise%20standing%20outside%20salon%20signage_20251006_181712_0006.png",
      alt: "Hair Makeover Specialist Salon Owner"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_franchise-phoenix/artifacts/z9p2oxhz_Banners%20for%20blogs%20%20A%20Double%20Standard%20for%20Black%20Entrepreneurs_20251109_170450_0004.png",
      alt: "Become a Consultant Specialist"
    }
  ];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  }, [carouselImages.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  // Auto-advance carousel
  useEffect(() => {
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  const faqs = [
    {
      question: "Do I need beauty experience?",
      answer: "No. Full training is provided covering all technical and business aspects."
    },
    {
      question: "How fast can I launch?",
      answer: "Typically 8–12 weeks depending on location and setup requirements."
    },
    {
      question: "What is the investment?",
      answer: "The franchise starts at £68,000, with optional packages for different salon sizes."
    },
    {
      question: "Is ongoing support included?",
      answer: "Yes — marketing, systems, operations, refresher training, and continuous guidance."
    },
    {
      question: "Will you help me get clients?",
      answer: "Yes. Launch marketing, social media campaigns, local advertising, and AI-powered booking system included."
    }
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  const handleCTAClick = () => {
    // Add UTM tracking for AI referrals
    const params = new URLSearchParams(window.location.search);
    let targetUrl = '/?utm_source=chatgpt.com&utm_medium=ai-referral&utm_campaign=chatgpt-discovery#apply-form';
    
    if (params.get('utm_source')) {
      targetUrl = `/?${params.toString()}#apply-form`;
    }
    
    navigate(targetUrl);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <SEO page="franchiseOpportunity" />
      <Header onApplyClick={handleCTAClick} />
      
      {/* Image Carousel */}
      <div className="relative w-full h-[500px] overflow-hidden mt-20">
        {/* Carousel Images */}
        <div className="relative w-full h-full">
          {carouselImages.map((image, index) => (
            <div
              key={index}
              className={`absolute w-full h-full transition-opacity duration-700 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover"
              />
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition-all z-10"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-6 h-6 text-dark" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition-all z-10"
          aria-label="Next slide"
        >
          <ChevronRight className="w-6 h-6 text-dark" />
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
          {carouselImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide
                  ? 'bg-white w-8'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Carousel Caption */}
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-center z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-2 text-dark">Our Successful Franchise Owners</h2>
          <p className="text-lg text-dark">Join our award-winning network of specialist salons</p>
        </div>
      </div>
      
      {/* Awards Carousel Section */}
      <div className="bg-gradient-to-r from-accent-primary to-accent-primary/80 py-16">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-4xl font-bold text-white text-center mb-4">Award-Winning Excellence</h2>
          <p className="text-xl text-white text-center mb-12">Recognized for Outstanding Service & Innovation Since 2017</p>
          
          <div className="relative overflow-hidden">
            <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide">
              {[
                { year: '2017', org: 'Salon Awards', awards: ['🏆 Best Hair Extensions Salon', '🏆 Best Customer Experience'] },
                { year: '2018', org: 'Salon Awards', awards: ['🏆 Best Designed Salon', '🏆 Best Customer Services', '🏆 Best Hygiene and Cleanliness', '🏆 Best Recommended Salon'] },
                { year: '2019', org: 'Industry Awards', awards: ['🥈 Finalist - Midlands Beauty Industry', '🥈 Finalist - Great British Hair & Beauty'] },
                { year: '2020', org: 'Salon Awards', awards: ['🏆 Winner - Best Hair Extension Salon', '🏆 Winner - Best Creative Image', '🥈 Finalist - Best Customer Experience', '🥈 Finalist - Best Salon West Midlands'] },
                { year: '2022', org: 'Multiple Awards', awards: ['🏆 Winner - Best Hair Extensions Salon', '🏆 Mobile & Emerging Technology StartUp'] },
                { year: '2024', org: 'National Recognition', awards: ['⭐ Fresha - Most Highly Recommended', '🏆 UK Hair & Beauty - Hairloss Specialist Winner'] },
                { year: '2025', org: 'HBA Awards', awards: ['🥈 Finalist - Hair Extensions Specialist', '🏅 Top 50 Salon UK', '⭐ Most Recommended Salon UK'] },
                { year: '2025', org: 'UK Hair & Beauty', awards: ['🏆 Winner - Hair Extensions & Hairloss Specialist', '🏆 Winner - Hairloss Replacement Specialist'] },
                { year: '2025', org: 'Salon Awards', awards: ['🏆 Winner - Best Hair Extensions Salon', '🏆 Winner - Best Hair Extensions Artist', '🥈 Finalist - Best Customer Experience', '🥈 Finalist - Creative Image'] },
                { year: '2025', org: 'Recognition', awards: ['🏆 Women Like Me Awards', '⭐ Fresha - Most Highly Recommended', '🏆 BTG Awards - Hair Extensions Specialist'] }
              ].map((award, index) => (
                <div key={index} className="flex-shrink-0 w-80 bg-white rounded-xl p-6 shadow-xl hover:shadow-2xl transition-shadow duration-300">
                  <div className="text-5xl font-bold text-accent-primary mb-2">{award.year}</div>
                  <div className="border-t-4 border-accent-primary pt-4">
                    <p className="text-lg font-bold text-dark mb-3">{award.org}</p>
                    {award.awards.map((awardItem, idx) => (
                      <p key={idx} className="text-gray-600 mb-1">{awardItem}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <p className="text-white text-center mt-8 text-lg italic">Scroll to see all our achievements →</p>
        </div>
      </div>
      
      <div className="pt-16 pb-16">
        <div className="container mx-auto px-6">
          
          {/* Hero Section */}
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-dark mb-6">
              Start a High-Demand 
              <span className="block text-accent-primary">Hair Extensions & Hair Loss Franchise</span>
            </h1>
            <p className="text-xl text-subtle mb-8 leading-relaxed">
              A proven beauty business with high ROI, full training, and a recession-resistant model.
            </p>
            <Button
              onClick={handleCTAClick}
              size="lg"
              className="bg-accent-primary hover:bg-accent-primary/90 text-lg px-12 py-4 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 font-semibold"
              style={{ color: 'white' }}
            >
              Request Franchise Information Pack
            </Button>
          </div>

          {/* Why Choose Hair Extensions Franchise */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
            <h2 className="text-3xl font-bold text-dark mb-6">
              Why Choose a Hair Extensions & Hair Loss Franchise?
            </h2>
            <p className="text-lg text-subtle mb-6">
              Demand for hair extensions, wigs, and hair-loss solutions is growing rapidly. More clients want personalised, long-lasting results from trained specialists. This sector is one of the few that is:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-center">
                <TrendingUp className="w-6 h-6 text-accent-primary mr-3" />
                <span>Recession-resistant</span>
              </div>
              <div className="flex items-center">
                <Award className="w-6 h-6 text-accent-primary mr-3" />
                <span>Skill-based & high-margin</span>
              </div>
              <div className="flex items-center">
                <Users className="w-6 h-6 text-accent-primary mr-3" />
                <span>Constantly in demand</span>
              </div>
            </div>
            <p className="text-lg text-subtle mt-6">
              The global hair extensions and hair-enhancement market is expanding faster than traditional beauty services — making this one of the most profitable franchise sectors today.
            </p>
          </div>

          {/* Quiz Assessment Banner */}
          <a 
            href="https://www.qualiquiz.app/quiz/salon-franchise-candidate-pre-qualification-asses"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full cursor-pointer hover:opacity-95 transition-opacity mb-12"
            data-testid="quiz-banner-opportunity"
          >
            <img 
              src="https://customer-assets-lqy194kg.emergentagent.net/job_f194cab7-0920-47a1-bac9-625d43d4e059/artifacts/79q94yoi_Franchise%20Candidatepre%20assessment%20needahairmakeover%203.jpg"
              alt="Salon Franchise Candidate Pre-Qualification Assessment - Are you ready to own a specialist salon? Take the Assessment"
              className="w-full h-auto rounded-2xl shadow-lg"
            />
          </a>

          {/* Why Need a Hair Makeover */}
          <div className="bg-accent-primary/5 rounded-2xl p-8 mb-12 border border-accent-primary/20">
            <h2 className="text-3xl font-bold text-dark mb-6">Why Need a Hair Makeover®?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-accent-primary mr-3 mt-1" />
                  <span>25+ years of expertise</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-accent-primary mr-3 mt-1" />
                  <span>National award-winning salon</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-accent-primary mr-3 mt-1" />
                  <span>18+ proprietary techniques</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-accent-primary mr-3 mt-1" />
                  <span>3,900+ professionals trained</span>
                </li>
              </ul>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-accent-primary mr-3 mt-1" />
                  <span>High client retention rates</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-accent-primary mr-3 mt-1" />
                  <span>Recognisable premium brand</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-accent-primary mr-3 mt-1" />
                  <span>Full setup + ongoing support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-accent-primary mr-3 mt-1" />
                  <span>Proven income generation model</span>
                </li>
              </ul>
            </div>
            <p className="text-lg font-semibold text-accent-primary mt-6">
              You do not need prior beauty experience. Everything is included in your training and launch plan.
            </p>
          </div>

          {/* Target Audience */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
            <h2 className="text-3xl font-bold text-dark mb-6">Who Is This Franchise For?</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-dark mb-4">Perfect for:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Users className="w-5 h-5 text-accent-primary mr-3 mt-1" />
                    <span>Beauty professionals seeking expansion</span>
                  </li>
                  <li className="flex items-start">
                    <TrendingUp className="w-5 h-5 text-accent-primary mr-3 mt-1" />
                    <span>Entrepreneurs looking for profitable business model</span>
                  </li>
                  <li className="flex items-start">
                    <Users className="w-5 h-5 text-accent-primary mr-3 mt-1" />
                    <span>Parents investing in graduates</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-dark mb-4">Also ideal for:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Award className="w-5 h-5 text-accent-primary mr-3 mt-1" />
                    <span>Career changers wanting control & freedom</span>
                  </li>
                  <li className="flex items-start">
                    <TrendingUp className="w-5 h-5 text-accent-primary mr-3 mt-1" />
                    <span>Investors seeking recession-proof industries</span>
                  </li>
                  <li className="flex items-start">
                    <Globe className="w-5 h-5 text-accent-primary mr-3 mt-1" />
                    <span>Individuals wanting scalable business</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Earning Potential */}
          <div className="bg-gradient-to-r from-accent-primary/10 to-accent-primary/5 rounded-2xl p-8 mb-12 border border-accent-primary/20">
            <h2 className="text-3xl font-bold text-dark mb-6">How Much Can Franchisees Earn?</h2>
            <p className="text-lg text-subtle mb-6">Income varies by location and effort, but on average:</p>
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white rounded-lg p-6 text-center">
                <div className="text-2xl font-bold text-accent-primary mb-2">£1,000/month</div>
                <div className="text-sm text-subtle">6 clients weekly</div>
              </div>
              <div className="bg-white rounded-lg p-6 text-center">
                <div className="text-2xl font-bold text-accent-primary mb-2">£3,500-£6,000/month</div>
                <div className="text-sm text-subtle">15-20 clients weekly</div>
              </div>
              <div className="bg-white rounded-lg p-6 text-center">
                <div className="text-2xl font-bold text-accent-primary mb-2">Additional Revenue</div>
                <div className="text-sm text-subtle">Hair loss services, retail, training</div>
              </div>
            </div>
            <p className="text-lg font-semibold text-accent-primary">
              This franchise offers one of the most profitable ROI structures in beauty today.
            </p>
          </div>

          {/* What's Included */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
            <h2 className="text-3xl font-bold text-dark mb-6">What's Included in the Franchise Package?</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-dark mb-4">Training & Certification:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-accent-primary mr-2 mt-1" />
                    <span>Full Hair Extensions Mastery Training</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-accent-primary mr-2 mt-1" />
                    <span>Hair Loss Non-Invasive Systems Certification</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-accent-primary mr-2 mt-1" />
                    <span>Business setup and location guidance</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-dark mb-4">Business Support:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-accent-primary mr-2 mt-1" />
                    <span>Website & AI-powered booking tools</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-accent-primary mr-2 mt-1" />
                    <span>Marketing & launch campaigns</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-accent-primary mr-2 mt-1" />
                    <span>Ongoing mentorship & support</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="bg-accent-primary/10 rounded-lg p-6 mt-6">
              <p className="text-lg font-semibold text-accent-primary text-center">
                Your franchise is fully supported from day one.
              </p>
            </div>
          </div>

          {/* Territories */}
          <div className="bg-accent-primary text-dark rounded-2xl p-8 mb-12">
            <h2 className="text-3xl font-bold mb-6 text-dark">Supported Territories (UK + Global)</h2>
            <p className="text-xl mb-6 text-dark">We provide opportunities across:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="flex items-center text-dark">
                <Globe className="w-5 h-5 mr-2" />
                <span>United Kingdom</span>
              </div>
              <div className="flex items-center text-dark">
                <Globe className="w-5 h-5 mr-2" />
                <span>Dubai & GCC</span>
              </div>
              <div className="flex items-center text-dark">
                <Globe className="w-5 h-5 mr-2" />
                <span>Europe</span>
              </div>
              <div className="flex items-center text-dark">
                <Globe className="w-5 h-5 mr-2" />
                <span>BRICS Locations</span>
              </div>
            </div>
            <p className="text-lg text-dark">International enquiries welcome.</p>
          </div>

          {/* FAQ - Accordion Style with Video */}
          <div className="grid md:grid-cols-12 gap-8 mb-12">
            {/* Video Section - Flush Left */}
            <div className="md:col-span-4">
              <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-24">
                <h3 className="text-xl font-bold text-dark mb-4">Watch Our Franchise Overview</h3>
                <div className="relative w-full" style={{ paddingBottom: '177.78%' }}>
                  <iframe
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                    src="https://www.youtube.com/embed/zdJQ0YVkQWo"
                    title="Franchise Overview Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <p className="text-sm text-subtle mt-4 text-center">Learn more about our franchise opportunity</p>
              </div>
            </div>

            {/* FAQ Section - Right Side */}
            <div className="md:col-span-8">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-3xl font-bold text-dark mb-8">Frequently Asked Questions</h2>
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div 
                      key={index} 
                      className="border border-gray-200 rounded-lg overflow-hidden transition-all duration-200 hover:border-accent-primary"
                    >
                      <button
                        onClick={() => toggleFAQ(index)}
                        className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors duration-200"
                      >
                        <div className="flex items-start space-x-3">
                          <span className="text-accent-primary text-xl font-bold mt-0.5">❓</span>
                          <h3 className="text-lg font-semibold text-dark pr-4">{faq.question}</h3>
                        </div>
                        <ChevronDown 
                          className={`w-5 h-5 text-accent-primary flex-shrink-0 transition-transform duration-200 ${
                            openFAQ === index ? 'transform rotate-180' : ''
                          }`}
                        />
                      </button>
                      <div 
                        className={`transition-all duration-300 ease-in-out ${
                          openFAQ === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        } overflow-hidden`}
                      >
                        <div className="px-5 pb-5 pt-2 bg-gray-50">
                          <p className="text-subtle leading-relaxed pl-8">{faq.answer}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-gradient-to-r from-accent-primary to-accent-primary/80 rounded-2xl p-12 text-center text-dark">
            <h2 className="text-4xl font-bold mb-6 text-dark">Ready to Start Your Hair Extensions & Hair Loss Franchise?</h2>
            <p className="text-xl mb-8 text-dark">This is the perfect time to enter the fastest-growing segment of the beauty industry.</p>
            <div className="space-y-4 md:space-y-0 md:space-x-4 md:flex md:justify-center">
              <Button
                onClick={handleCTAClick}
                size="lg"
                className="bg-white text-accent-primary hover:bg-gray-100 text-lg px-12 py-4 rounded-full font-semibold"
              >
                📄 Request Franchise Information Pack
              </Button>
              <a
                href="tel:+442030514338"
                className="inline-flex items-center justify-center bg-white/20 text-dark border-2 border-white hover:bg-white hover:text-accent-primary text-lg px-12 py-4 rounded-full font-semibold transition-all duration-300"
              >
                <Phone className="w-5 h-5 mr-2" />
                Book Consultation Call
              </a>
            </div>
            <p className="text-lg mt-6 opacity-90 text-dark">Join our waitlist for 2026–2027 locations</p>
          </div>

          {/* Looking for a Training Course Section */}
          <div className="mt-16 bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-3xl font-bold text-dark text-center mb-4">Looking for a Training Course?</h2>
            <p className="text-lg text-subtle text-center mb-8">
              Not ready for a franchise? Start with our professional hair extensions training courses.
            </p>
            
            {/* Training Course Carousel */}
            <div className="relative overflow-hidden mb-8">
              <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide">
                {[
                  {
                    url: "https://customer-assets.emergentagent.com/job_franchise-cta-fix/artifacts/f0pf2b1x_Hair%20extensions%20training%20coursestraining%20courses_20260109_152647_0000.png",
                    alt: "Hair Extensions Training Course"
                  },
                  {
                    url: "https://customer-assets.emergentagent.com/job_franchise-cta-fix/artifacts/ayhsds4r_Hair%20extensions%20training%20coursestraining%20courses_20260109_152647_0001.png",
                    alt: "Hair Extensions Academy Training"
                  },
                  {
                    url: "https://customer-assets.emergentagent.com/job_franchise-cta-fix/artifacts/jfdjbpu9_Hair%20extensions%20training%20coursestraining%20courses_20260109_152647_0002.png",
                    alt: "Professional Hair Extensions Course"
                  },
                  {
                    url: "https://customer-assets.emergentagent.com/job_franchise-cta-fix/artifacts/rm6uhig4_Hair%20extensions%20training%20coursestraining%20courses_20260109_152648_0006.png",
                    alt: "Hair Extensions 1, 2, 3 or 5 Day Course"
                  }
                ].map((image, index) => (
                  <div key={index} className="flex-shrink-0 w-80 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300">
                    <img
                      src={image.url}
                      alt={image.alt}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                ))}
              </div>
              <p className="text-subtle text-center mt-4 text-sm italic">Scroll to see more courses →</p>
            </div>
            
            {/* Training Course CTA */}
            <div className="text-center">
              <a
                href="https://www.hairextensionstraining.academy"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-accent-primary hover:bg-accent-primary/90 font-bold text-lg px-10 py-4 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300"
                style={{ color: 'white' }}
              >
                Explore Training Courses
              </a>
            </div>
          </div>

        </div>
      </div>
      
      <Footer />
    </div>
  );
};